import {
	Column,
	Conditional,
	Else,
	Heading,
	If,
	List,
	makeClassName,
	Modal,
	ModalProps,
	Paragraph,
	Row,
	Rule,
	Section,
} from "@hex-insights/core";
import { Icon } from "../../../../Components/Icon";
import { ParentPortalReEnrollmentContractsQuery } from "../../../../Utilities";
import { StudentFormat } from "../../../Format";
import { FileAttachmentView } from "../../MediaAttachment";
import { StudentIcon } from "../../Student";
import { ContractSignatureLabel } from "../Display/SignatureLabel";
import styles from "./styles.module.css";

export type ContractsGuardianDownloadModalProps = {
	isLoading?: boolean;
	data: ParentPortalReEnrollmentContractsQuery | undefined;
} & Pick<ModalProps, "ifRef" | "onClose" | "className" | "style">;

export function ContractsGuardianDownloadModal({
	isLoading = false,
	data,
	ifRef,
	onClose,
	className,
	style,
}: ContractsGuardianDownloadModalProps) {
	return (
		<Modal ifRef={ifRef} onClose={onClose} className={makeClassName(styles["modal"], className)} style={style}>
			<Modal.Header className={styles["modal__header"]}>
				<Heading.H2 noMargin>2024-2025 Contracts</Heading.H2>
			</Modal.Header>
			<Modal.Body>
				<Section>
					<Section.Body>
						<Paragraph>
							Your signature is required in <strong>five (5)</strong> places on each of the following documents. Those
							places are:
						</Paragraph>

						<List>
							<List.Item>Special Terms And Conditions - Section 1</List.Item>
							<List.Item>Appendix 1</List.Item>
							<List.Item>Appendix 2: Medical Information 1</List.Item>
							<List.Item>Appendix 2: Medical Information 2</List.Item>
							<List.Item>General Terms And Conditions - Section 2</List.Item>
						</List>

						<Paragraph>Look for the following symbol for each signature location:</Paragraph>

						<ContractSignatureLabel style={{ width: "fit-content", margin: "0 auto" }} />
					</Section.Body>
				</Section>

				<Rule thickness="2px" style={{ marginBottom: "1.5rem" }} />

				<Conditional>
					<If condition={isLoading && !data}>
						<span>Loading...</span>
					</If>
					<Else>
						<Column justify="spaced-start" verticalSpacing="1rem">
							{data?.contractConnection.edges.map((e) => (
								<ContractItem key={e.node.id} contract={e.node} />
							))}
						</Column>
					</Else>
				</Conditional>
			</Modal.Body>
		</Modal>
	);
}

type ContractItemProps = {
	contract: ParentPortalReEnrollmentContractsQuery["contractConnection"]["edges"][0]["node"];
};

function ContractItem({ contract }: ContractItemProps) {
	const fileName = contract.name;

	return (
		<Column justify="spaced-start" verticalSpacing="0.5rem" className={styles["contract-item"]}>
			<Row justify="spaced-start" align="center" horizontalSpacing="0.5rem">
				<StudentIcon student={contract.student} size={32} />
				<span className={styles["contract-item__student-name"]}>{StudentFormat.name(contract.student)}</span>
			</Row>

			<FileAttachmentView
				title={fileName}
				fileURL={contract.fileURL}
				fileContentType={contract.fileContentType}
				downloadLabel="full"
			/>

			<If condition={contract.status === "Active"}>
				<Row
					justify="spaced-start"
					align="center"
					horizontalSpacing="0.25rem"
					className={styles["contract-item__success-message"]}
				>
					<Icon.Check size={14} /> <span>We've received your signed contract.</span>
				</Row>
			</If>
		</Column>
	);
}
